import React, { useState, useEffect } from "react";
import { Input, Button, Tag, message } from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { accountPaymentHelpers, paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["bank_name", "is_active"];

export default function PaymentAccountTable() {
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 1,
    });
    const [searchText, setSearchText] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        sort_by: "created_at",
        sort_order: "desc",
    });

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    // Fetch data using the proper API
    const fetchAccounts = async (
        page = 1,
        perPage = 10,
        search = "",
        status = ""
    ) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: perPage,
                search: search.trim(),
                status,
                sort_by: filters.sort_by,
                sort_order: filters.sort_order,
            };

            const response = await paymentAccountAPIWithErrorHandling.getLists(params);
            const { accounts: accountData, pagination: paginationData } = accountPaymentHelpers.extractAccountPaymentList(response);

            setAccounts(accountData);
            setPagination({
                current: paginationData.current,
                pageSize: paginationData.pageSize,
                total: paginationData.total,
                from: paginationData.from,
                to: paginationData.to,
                lastPage: paginationData.lastPage,
            });
        } catch (error) {
            message.error(error.message || "Không thể tải danh sách tài khoản");
            console.error("Error fetching:", error);
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle table pagination change
    const handleTableChange = (paginationConfig, tableFilters, sorter) => {
        // Handle sorting
        if (sorter && sorter.field) {
            const sortBy = sorter.field;
            const sortOrder = sorter.order === "descend" ? "desc" : "asc";
            setFilters((prev) => ({
                ...prev,
                sort_by: sortBy,
                sort_order: sortOrder,
            }));
        }

        // Handle status filter
        let statusFilter = "";
        if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
            statusFilter = tableFilters.status[0] ? "active" : "inactive";
        }

        fetchAccounts(
            paginationConfig.current,
            paginationConfig.pageSize,
            searchText,
            statusFilter
        );
    };

    // Handle search
    const handleSearch = (value) => {
        setSearchText(value);
        fetchAccounts(1, pagination.pageSize, value, filters.status);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchText("");
        fetchAccounts(1, pagination.pageSize, "", filters.status);
    };

    // Action handlers
    const handleEdit = (record) => {
        message.info(`Chỉnh sửa thông tin tài khoản: ${record.name}`);
        // Navigate to edit page or open edit modal
        navigate(`/payment-account/${record.id}`);
    };

    const handleDelete = async (record) => {
        try {
            const confirmed = await confirmDelete({ entity: "tài khoản", name: record.name });
            if (!confirmed) return;

            await paymentAccountAPIWithErrorHandling.deleteAccount(record.id);
            message.success("Xóa thông tin tài khoản thành công");

            // Refresh data
            fetchAccounts(
                pagination.current,
                pagination.pageSize,
                searchText,
                filters.status
            );
        } catch (error) {
            message.error(error.message || "Không thể xóa tài khoản");
        }
    };

    const handleAdd = () => {
        message.info("Thêm thông tin tài khoản mới");
        navigate("/store-payment-account");
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT", key: "index", width: "5%", align: "center",
                render: (_, __, index) =>
                    (pagination.current - 1) * pagination.pageSize + index + 1,
            },
        },
        {
            key: "name", label: "Tên tài sản", fixed: true,
            column: { title: "Tên tài sản", dataIndex: "name", key: "name", width: "15%", sorter: true },
        },
        {
            key: "bank_name", label: "Ngân hàng",
            column: {
                title: "Ngân hàng", dataIndex: "bank_name", key: "bank_name", ellipsis: true, width: "30%",
                render: (text) => text || "-",
            },
        },
        {
            key: "is_active", label: "Trạng thái",
            column: {
                title: "Trạng thái", dataIndex: "is_active", key: "is_active", width: "10%",
                render: (is_active) => (
                    <Tag color={is_active ? "green" : "red"}>
                        {is_active ? "Hoạt động" : "Không hoạt động"}
                    </Tag>
                ),
                filters: [
                    { text: "Hoạt động", value: true },
                    { text: "Không hoạt động", value: false },
                ],
                filterMultiple: false,
            },
        },
        actionColumnDef(
            (record) => [
                { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => handleEdit(record) },
                { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
            ],
            { width: 100 }
        ),
    ];

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Input.Search
                    placeholder="Tìm kiếm theo tên, mã,..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    enterButton={<SearchOutlined />}
                    allowClear
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = !!searchText;

    return (
        <BrandedPageLayout
            title="Tài khoản thanh toán"
            subtitle="Quản lý danh sách tài khoản thanh toán."
            breadcrumbItems={[{ title: "Tài khoản thanh toán" }, { title: "Danh sách" }]}
        >
            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleClearSearch}
                onReload={() => fetchAccounts(pagination.current, pagination.pageSize, searchText, filters.status)}
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Thêm mới
                    </Button>
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={accounts}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có dữ liệu"
            />
        </BrandedPageLayout>
    );
}
